import type { JsonObject } from '@seen/core/json'
import { generateHTML } from '@tiptap/core'
import type { JSONContent } from '@tiptap/react'
import { getRenderExtensions } from '#src/components/rich-text-editor/extensions.ts'
import {
  extractFirstLineFromHTML,
  extractTextFromHTML,
} from '#src/utils/html.ts'
import { parseNoteContent } from '#src/utils/noteContent.ts'

function readVisibleAttrLabel(attrs: JSONContent['attrs']): string | undefined {
  if (!attrs || typeof attrs !== 'object') {
    return undefined
  }
  const record = attrs as JsonObject
  for (const key of ['label', 'display', 'name', 'text'] as const) {
    const value = record[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
    }
  }
  return undefined
}

function extractTextFromTipTapJson(node: JSONContent): string {
  if (node.type === 'text' && node.text) {
    return node.text
  }
  const attrLabel = readVisibleAttrLabel(node.attrs)
  const childText = node.content?.map(extractTextFromTipTapJson).join('') ?? ''
  if (attrLabel) {
    return childText ? `${attrLabel}${childText}` : attrLabel
  }
  return childText
}

// Global /notes indexes every note body for the content filter and title cells.
// Some stored docs include TipTap node types the render extension set does not
// register yet; generateHTML throws on those. Fall back to plain text for list
// views only — contentToHTML (editor seeding) is unchanged and still throws.
function htmlForNotesListTextExtraction(content: string): string {
  const parsed = parseNoteContent(content)
  if (typeof parsed === 'string') {
    return parsed
  }
  try {
    return generateHTML(parsed, getRenderExtensions())
  } catch {
    return extractTextFromTipTapJson(parsed)
  }
}

export function extractFirstLineForNotesList(content: string): string {
  return extractFirstLineFromHTML(htmlForNotesListTextExtraction(content))
}

export function extractTextForNotesList(content: string): string {
  return extractTextFromHTML(htmlForNotesListTextExtraction(content))
}
